import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppIcon, AppIcons } from "./AppIcons";
import { AppLiveReaction, AppLiveReactRules, AppReactPill, AppReactState } from "./AppLiveEvents";
import { AppReactions, IRxDoc, IRxNode } from "./AppReactions";
import { AppReactRules } from "./AppReactRules";

// AppLiveReact - A REACTION SHOWS ON ITS BUBBLE WITHIN SECONDS (2026-10-01, owner-picked "Live reactions, edits,
// links"): the contact's, and the owner's own from their phone. `AppLiveEvents` is the rule (the fold); this is the act.
//
// ⭐ THE PILL ON THE BUBBLE IS THE STATE. Each event is folded onto what the bubble's pill says NOW (`data-mine`,
//    `data-others`, `data-otherswho`, and which face comes first) and the pill is redrawn in the page's own shape
//    (`ReactionBook.Html`: `span.rx`, our SVG faces, the count, the tooltip, the same data attributes) - so a pill the
//    rebuild wrote, one this app drew optimistically and one drawn live are the same thing to the next event, to
//    `AppReactions` (which reads `data-mine` to know what a tap means) and to the next rebuild.
// ⛔ NEWER WINS BY TIME: the real time of every event applied is remembered per message and side, so an older event
//    that arrives later (the feed replays what is at or after its floor when a page opens) never undoes a newer one.
// ⭐ THE OWNER'S OWN OPTIMISTIC REACTION (`AppReactions`, faded until its receipt): the echo of that very reaction
//    CONFIRMS it (`Echoed` - the pill stops being faded, no second count: it is a state, not a tally); anything else
//    for that message waits until the reaction settles - sent, unconfirmed or refused (`OnSettled`) - because the
//    reactor may still put the bubble's ORIGINAL pill back, node for node.
// ⭐ ANY BUBBLE WITH A WHATSAPP ID: the page's own, a live one, and the owner's just-sent one once its echo gave it a
//    `data-mid` (`AppChatAppend.AdoptSent`). A reaction to a message that is not on the page is dropped.
// ⛔ BUILT, NEVER PARSED: nodes by `createElement`, text by `textContent`, an emoji only CHOOSES one of our icons.
// ⛔ READ-ONLY (decision 9): nothing here can reach a sender - it never calls `AppReactions.Pick`.
// ⚠ SHAPED BY INTERFACES (`IRx*`), so the suite drives it with a small fake document.

/** What this needs of the reactor (`AppReactions` fits). */
export interface ILiveReactor {
    /** The emoji of the owner's reaction on its way for this message ("" = taking it off), or null when none is. */
    PendingNext(Mid: string): string | null;
    /** The echo of that reaction arrived: it is confirmed. */
    Echoed(Mid: string): void;
    /** Told when a reaction on its way settled (`Kept` = it stands; false = refused, the old pill is back). */
    OnSettled: ((Mid: string, Kept: boolean) => void) | null;
}

/** The frame's document, as this needs it: the reactor's, plus finding the bubbles. A real `Document` fits. */
export interface ILiveReactDoc extends IRxDoc {
    querySelectorAll(Selector: string): ArrayLike<IRxNode>;
}

/** The real times last applied to one message's two seats (0 = none yet). */
class AppLiveReactTimes {
    public Mine: number = 0;
    public Theirs: number = 0;
}

export class AppLiveReact {
    private Reactor: ILiveReactor | null;
    /** The frame's document right now, or null - asked when a reaction settles (the reactor has no document to hand over). */
    private PageOf: () => ILiveReactDoc | null;
    private Times: Record<string, AppLiveReactTimes> = {};
    /** Events held back while the owner's own reaction on that message is on its way. */
    private Deferred: Record<string, AppLiveReaction[]> = {};
    /** How many pills were drawn or removed - for the suite and the harness. */
    public Drawn: number = 0;

    public constructor(Reactor: ILiveReactor | null, PageOf: () => ILiveReactDoc | null) {
        this.Reactor = Reactor;
        this.PageOf = PageOf;
        if (Reactor !== null) { Reactor.OnSettled = this.OnSettled; }
    }

    /** One answer's reaction events, oldest first. Answers how many bubbles were redrawn. */
    public Apply = (Doc: ILiveReactDoc, Events: AppLiveReaction[]): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Done: number = 0;
            for (const Ev of Events) {
                if (this.Hold(Ev) === true) { continue; }
                if (this.One(Doc, Ev) === true) { Done = Done + 1; }
            }
            return Done;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.Apply");
            return 0;
        }
    };

    /**
     * Is this event held back for now? While the owner's own reaction on that message is on its way: its ECHO
     * confirms it (and is then applied); every other event for the message waits for `OnSettled`.
     */
    private Hold = (Ev: AppLiveReaction): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            if (this.Reactor === null) { return false; }
            const Next: string | null = (this.Reactor as ILiveReactor).PendingNext(Ev.To);
            if (Next === null) { return false; }
            if (Ev.Me === true && AppReactRules.Plain(Ev.Emoji) === AppReactRules.Plain(Next as string)) {
                (this.Reactor as ILiveReactor).Echoed(Ev.To);
                return false;
            }
            if (this.Deferred[Ev.To] === undefined) { this.Deferred[Ev.To] = []; }
            this.Deferred[Ev.To].push(Ev);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.Hold");
            return true;
        }
    };

    /**
     * The owner's reaction on `Mid` settled; what waited for it is applied to the pill the reactor left on the bubble.
     * ⛔ KEPT (sent / unconfirmed): the owner's OWN events that waited are dropped - they were on the way while the
     * owner chose this one in the app, so this one is the newer choice (and no clock of ours is compared with
     * WhatsApp's). REFUSED: the original pill is back, and everything that waited is applied to it.
     */
    public OnSettled = (Mid: string, Kept: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Waiting: AppLiveReaction[] | undefined = this.Deferred[Mid];
            delete this.Deferred[Mid];
            const Doc: ILiveReactDoc | null = this.PageOf();
            if (Waiting === undefined || Doc === null) { return; }
            for (const Ev of Waiting) {
                if (Kept === true && Ev.Me === true) { continue; }
                this.One(Doc as ILiveReactDoc, Ev);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.OnSettled");
        }
    };

    /** One event onto its bubble. True = the pill changed. */
    private One = (Doc: ILiveReactDoc, Ev: AppLiveReaction): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Bubble: IRxNode | null = AppLiveReact.BubbleFor(Doc, Ev.To);
            if (Bubble === null) { return false; }
            const Old: IRxNode | null = (Bubble as IRxNode).querySelector(".rx");
            const State: AppReactState = this.StateOf(Old, Ev.To);
            const Changed: boolean = AppLiveReactRules.Apply(State, Ev);
            // The real times are kept whether or not anything is drawn: an event that only confirms what the pill
            // shows still dates that seat.
            const Times: AppLiveReactTimes = this.TimesOf(Ev.To);
            Times.Mine = Math.max(Times.Mine, AppLiveReact.Real(State.Mine.At));
            Times.Theirs = Math.max(Times.Theirs, AppLiveReact.Real(State.Theirs.At));
            if (Changed === false) { return false; }
            this.Draw(Doc, Bubble as IRxNode, Old, AppLiveReactRules.Pill(State));
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.One");
            return false;
        }
    };

    /** A seat's time when it is a real one (a pseudo-time read off a drawn pill is 0 or 1). DOCUMENTED EXCEPTION: pure.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static Real = (At: number): number => {
        if (At > 1) { return At; }
        return 0;
    };

    private TimesOf = (Mid: string): AppLiveReactTimes => {
        if (getCoreUtils().KErrorMode === true) { return new AppLiveReactTimes(); }
        try {
            if (this.Times[Mid] === undefined) { this.Times[Mid] = new AppLiveReactTimes(); }
            return this.Times[Mid];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.TimesOf");
            return new AppLiveReactTimes();
        }
    };

    /** What the bubble's pill stands for now (`AppLiveReactRules.Seed`), with the real times this visit applied. */
    private StateOf = (Pill: IRxNode | null, Mid: string): AppReactState => {
        if (getCoreUtils().KErrorMode === true) { return new AppReactState(); }
        try {
            let State: AppReactState = new AppReactState();
            if (Pill !== null) {
                const Mine: string | null = (Pill as IRxNode).getAttribute(AppReactRules.MineAttr);
                State = AppLiveReactRules.Seed(Mine, (Pill as IRxNode).getAttribute(AppReactRules.OthersAttr),
                    (Pill as IRxNode).getAttribute(AppReactRules.OthersWhoAttr), AppLiveReact.MineFirst(Pill as IRxNode, nz(Mine)));
            }
            const Known: AppLiveReactTimes | undefined = this.Times[Mid];
            if (Known === undefined) { return State; }
            if (Known.Mine > 0) { State.Mine.At = Known.Mine; }
            if (Known.Theirs > 0) { State.Theirs.At = Known.Theirs; }
            return State;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.StateOf");
            return new AppReactState();
        }
    };

    /**
     * Is the pill's FIRST face the owner's emoji? (The pill draws the earlier reaction first; with one face, or the
     * same emoji twice, the order says nothing and false is as good.) An icon is known by its label, any other
     * emoji by its text.
     */
    private static MineFirst = (Pill: IRxNode, Mine: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Mine.length === 0) { return false; }
            const First: IRxNode | null | undefined = (Pill as unknown as { firstElementChild?: IRxNode | null }).firstElementChild;
            if (First === null || First === undefined) { return false; }
            const Icon: AppIcon | null = AppIcons.ForEmoji(Mine);
            if (Icon !== null) { return nz((First as IRxNode).getAttribute("aria-label")) === (Icon as AppIcon).Label; }
            return AppReactRules.Plain(nz((First as IRxNode).textContent)) === AppReactRules.Plain(Mine);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.MineFirst");
            return false;
        }
    };

    /** The bubble with this `data-mid`. ⛔ Compared in a loop - the id never enters a selector. */
    private static BubbleFor = (Doc: ILiveReactDoc, Mid: string): IRxNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const All: ArrayLike<IRxNode> = Doc.querySelectorAll("[data-mid]");
            for (let At: number = 0; At < All.length; At = At + 1) {
                if (All[At].getAttribute("data-mid") === Mid) { return All[At]; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.BubbleFor");
            return null;
        }
    };

    /** The old pill out, the new one in (or none), and the bubble's badge margin (`rxd`) to match. */
    private Draw = (Doc: IRxDoc, Bubble: IRxNode, Old: IRxNode | null, Pill: AppReactPill | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Old !== null) { Bubble.removeChild(Old as IRxNode); }
            this.Drawn = this.Drawn + 1;
            if (Pill === null) { Bubble.className = AppReactions.Classes(Bubble.className, "", "rxd"); return; }
            Bubble.className = AppReactions.Classes(Bubble.className, "rxd", "");
            Bubble.appendChild(AppLiveReact.PillNode(Doc, Pill as AppReactPill));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.Draw");
        }
    };

    /** `<span class="rx" title aria-label data-mine data-others data-otherswho>` faces + count - the page's own pill. */
    public static PillNode = (Doc: IRxDoc, Pill: AppReactPill): IRxNode => {
        const Out: IRxNode = Doc.createElement("span");
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.className = "rx";
            AppReactions.FillPill(Doc, Out, Pill);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveReact.PillNode");
            return Out;
        }
    };
}
